'use client'

import React, { useRef } from 'react'

import { HorizontalScrollControls } from '@/components/HorizontalScrollControls'
import { PoliticianCard } from '@/components/PoliticianCard'

import type { Legislator } from '@/payload-types'

function isSenator(legislator: Legislator): boolean {
  const role = legislator.bt50?.role ?? ''
  return /senator/i.test(role)
}

function isRepresentative(legislator: Legislator): boolean {
  const role = legislator.bt50?.role ?? ''
  return /representative|delegate|resident commissioner/i.test(role)
}

/**
 * State jurisdiction contact section for senators and representatives.
 */
export const RepsSection = ({ state, reps }: { state: string; reps: Legislator[] }) => {
  const senators = reps.filter(isSenator)
  const representatives = reps.filter(isRepresentative)

  const senatorsScrollRef = useRef<HTMLDivElement>(null)
  const representativesScrollRef = useRef<HTMLDivElement>(null)

  return (
    <div className="my-10 w-full overflow-hidden bg-accent-foreground-2 py-10 md:py-16">
      <div className="wrapper">
        <div className="mx-auto mb-6 max-w-[60ch] text-center md:mb-8">
          <h2 className="mb-3 font-display text-3xl font-normal lg:text-4xl xl:text-5xl">
            Contact your representatives <span className="text-accent-2">from {state}</span>
          </h2>
          <p className="font-body text-base font-medium text-foreground">
            Support MAHA&apos;s mission by taking action on the legislation that matters most to
            you. Find a bill above, then contact your state representative below to urge them to
            support MAHA&apos;s position.
          </p>
        </div>

        {senators.length > 0 && (
          <>
            <h3 className="mb-4 font-body text-xl font-semibold text-foreground lg:text-2xl">
              Senators
            </h3>
            <div
              ref={senatorsScrollRef}
              className="scrollbar-hidden -mr-[max(1rem,calc((100vw-var(--breakpoint-wrapper))/2+1rem))] flex snap-x snap-mandatory scroll-pl-0 items-stretch gap-4 overflow-x-auto pr-8"
              data-lenis-prevent-horizontal
            >
              {senators.map((senator, index) => (
                <div
                  key={`${index}-${senator.id}`}
                  className="flex w-full max-w-[394px] shrink-0 snap-start sm:w-[394px]"
                >
                  <PoliticianCard politician={senator} />
                </div>
              ))}
            </div>
            <HorizontalScrollControls
              className="my-4 justify-start md:hidden"
              hideWhenFits
              scrollRef={senatorsScrollRef as React.RefObject<HTMLDivElement>}
            />
          </>
        )}

        {representatives.length > 0 && (
          <>
            <h3 className="mt-8 mb-4 font-body text-xl font-semibold text-foreground lg:text-2xl">
              Representatives
            </h3>
            <div
              ref={representativesScrollRef}
              className="scrollbar-hidden -mr-[max(1rem,calc((100vw-var(--breakpoint-wrapper))/2+1rem))] flex snap-x snap-mandatory scroll-pl-0 items-stretch gap-4 overflow-x-auto pr-8"
              data-lenis-prevent-horizontal
            >
              {representatives.map((representative, index) => (
                <div
                  key={`${index}-${representative.id}`}
                  className="flex w-full max-w-[394px] shrink-0 snap-start sm:w-[394px]"
                >
                  <PoliticianCard politician={representative} />
                </div>
              ))}
            </div>
            <HorizontalScrollControls
              className="my-4 justify-start"
              hideWhenFits
              scrollRef={representativesScrollRef as React.RefObject<HTMLDivElement>}
            />
          </>
        )}
      </div>
    </div>
  )
}
